
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Form } from '@/components/ui/form';
import { Technique } from '@/types/technique';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createTechniqueSchema,
  CreateTechniqueFormValues,
  defaultFormValues,
  BasicInfoFields,
  TechniqueTypeFields,
  PostureField,
  DescriptionField,
  MediaUrlFields,
  KeyPointsField,
  CommonMistakesField,
  PersonalNotesField,
  FormActions
} from './create-technique-form';
import { updateUserTechnique } from '@/services';
import { toast } from '@/hooks/use-toast';

interface EditTechniqueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technique: Technique;
  notes: string;
  onSuccess?: () => void;
}

export function EditTechniqueDialog({
  open,
  onOpenChange,
  technique,
  notes,
  onSuccess,
}: EditTechniqueDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form: UseFormReturn<CreateTechniqueFormValues> = useForm<CreateTechniqueFormValues>({
    resolver: zodResolver(createTechniqueSchema),
    defaultValues: {
      ...defaultFormValues,
      name: technique.name,
      description: technique.description,
      techniquePosition: technique.technique_position,
      techniqueType: technique.technique_type,
      difficulty: technique.difficulty,
      imageUrl: technique.image_url || '',
      videoUrl: technique.video_url || '',
      posture: technique.category === 'offensive' ? 'offensive' : technique.category === 'defensive' ? 'defensive' : undefined,
      keyPoints: technique.key_points?.join(', ') || '',
      commonMistakes: technique.common_mistakes?.join(', ') || '',
      personalNotes: notes || '',
      isPrivate: technique.is_private || false,
    },
    mode: "onChange"
  });

  const onSubmit = async (values: CreateTechniqueFormValues) => {
    setIsSubmitting(true);
    try {
      // Convert comma-separated strings to arrays before submitting
      const keyPointsArray = values.keyPoints
        .split(',')
        .map((point: string) => point.trim())
        .filter(Boolean);
        
      const commonMistakesArray = values.commonMistakes
        .split(',')
        .map((mistake: string) => mistake.trim())
        .filter(Boolean);
      
      const techniqueData = {
        name: values.name,
        description: values.description,
        techniquePosition: values.techniquePosition,
        techniqueType: values.techniqueType,
        difficulty: values.difficulty,
        positionId: '', // We're removing position selection, so this is empty
        imageUrl: values.imageUrl || undefined,
        videoUrl: values.videoUrl || undefined,
        category: values.posture, // Map posture to category field
        subcategory: '', // Remove subcategory
        keyPoints: keyPointsArray,
        commonMistakes: commonMistakesArray,
        personalNotes: values.personalNotes || undefined,
        isPrivate: values.isPrivate,
        created_by_user_id: technique.created_by_user_id,
        is_user_created: technique.is_user_created,
      };

      // Update the technique using the updateUserTechnique service
      await updateUserTechnique(technique.id, techniqueData);

      toast({
        title: "Success",
        description: "Technique updated successfully",
      });

      // Trigger the onSuccess callback if it exists
      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred while updating the technique",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Technique</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info (Name & Privacy) */}
            <BasicInfoFields form={form} />

            {/* Technique Type Fields */}
            <TechniqueTypeFields form={form} />

            {/* Posture Field */}
            <PostureField form={form} />

            {/* Description Field */}
            <DescriptionField form={form} />

            {/* Media URL Fields */}
            <MediaUrlFields form={form} />

            {/* Key Points Field */}
            <KeyPointsField form={form} />

            {/* Common Mistakes Field */}
            <CommonMistakesField form={form} />

            {/* Personal Notes Field */}
            <PersonalNotesField form={form} />

            {/* Form Actions */}
            <FormActions 
              isSubmitting={isSubmitting} 
              onCancel={() => onOpenChange(false)}
              submitLabel="Update Technique"
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
